import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { api } from '../../api/client';
const KB_FILES = ['profile.md', 'services.md', 'audience.md', 'positioning.md', 'keywords.md'];
export default function IngestStep({ onAdvance, onChanged }) {
    const [inputs, setInputs] = useState(null);
    const [kb, setKb] = useState({});
    const [draft, setDraft] = useState({});
    const [extra, setExtra] = useState('');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [usage, setUsage] = useState('');
    async function refresh() {
        setError(null);
        setBusy('preview');
        try {
            const { inputs, kb } = await api.wizardIngestPreview();
            setInputs(inputs);
            setKb(kb);
            setDraft(kb);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load');
        }
        finally {
            setBusy(null);
            onChanged();
        }
    }
    useEffect(() => { refresh(); }, []);
    async function run(apply) {
        setBusy('run');
        setError(null);
        try {
            const r = await api.wizardIngestRun(apply, extra);
            setKb(r.files);
            setDraft(r.files);
            setUsage(r.usage ? JSON.stringify(r.usage) : '');
            if (apply)
                onChanged();
        }
        catch (e) {
            setError(e?.message ?? 'Run failed');
        }
        finally {
            setBusy(null);
        }
    }
    async function saveOne(file) {
        setBusy('save');
        setError(null);
        try {
            await api.wizardIngestSave(file, draft[file] ?? '');
            setKb((k) => ({ ...k, [file]: draft[file] ?? '' }));
            onChanged();
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(null);
        }
    }
    const hasKb = KB_FILES.every((f) => (kb[f] ?? '').trim() !== '');
    return (_jsxs("div", { className: "step-content", children: [_jsxs("section", { className: "card-section", children: [_jsx("h2", { children: "Inputs" }), !inputs && _jsx("div", { className: "muted", children: "Loading\u2026" }), inputs && (_jsxs("ul", { className: "input-summary", children: [_jsxs("li", { children: [_jsx("strong", { children: inputs.files.length }), " readable file", inputs.files.length === 1 ? '' : 's', " ", inputs.files.length > 0 && _jsxs("span", { className: "muted small", children: ["(", inputs.files.join(', '), ")"] })] }), _jsxs("li", { children: [_jsx("strong", { children: inputs.urls.length }), " URL", inputs.urls.length === 1 ? '' : 's'] }), inputs.skipped.length > 0 && (_jsxs("li", { className: "muted small", children: [inputs.skipped.length, " skipped (PDF/DOCX must be converted to .md/.txt server-side):", _jsx("ul", { children: inputs.skipped.map((s) => _jsx("li", { children: s }, s)) })] }))] })), _jsxs("label", { className: "field", children: [_jsx("span", { children: "Extra notes for the AI (optional)" }), _jsx("textarea", { rows: 4, value: extra, onChange: (e) => setExtra(e.target.value), placeholder: "Founder bio, recent achievements, anything that didn't fit a file\u2026" })] }), _jsxs("div", { className: "step-foot", children: [_jsx("button", { className: "ghost", onClick: () => run(false), disabled: busy !== null, children: busy === 'run' ? 'Running…' : 'Preview (don\'t save)' }), _jsx("button", { onClick: () => run(true), disabled: busy !== null, children: busy === 'run' ? 'Running…' : 'Run ingest & save' })] }), usage && _jsxs("div", { className: "muted small", children: ["Usage: ", _jsx("code", { children: usage })] })] }), _jsxs("section", { className: "card-section", children: [_jsx("h2", { children: "Brand knowledge base" }), _jsxs("p", { className: "muted small", children: ["Files generated by ingest, persisted at ", _jsx("code", { children: "site/brand/*.md" }), ". Edit inline \u2014 the rest of the wizard reads from these."] }), KB_FILES.map((f) => (_jsxs("details", { className: "kb-file", open: (draft[f] ?? '').trim() === '', children: [_jsxs("summary", { children: [f, " ", _jsxs("span", { className: "muted small", children: ["(", (draft[f] ?? '').length, " chars)"] })] }), _jsx("textarea", { className: "mono", rows: 10, value: draft[f] ?? '', onChange: (e) => setDraft((d) => ({ ...d, [f]: e.target.value })), placeholder: "(empty \u2014 run ingest above)" }), _jsx("div", { className: "step-foot", children: _jsxs("button", { className: "ghost", disabled: busy !== null || (draft[f] ?? '') === (kb[f] ?? ''), onClick: () => saveOne(f), children: ["Save ", f] }) })] }, f)))] }), error && _jsx("div", { className: "err small pad-y", children: error }), _jsx("div", { className: "step-foot", children: _jsx("button", { onClick: onAdvance, disabled: !hasKb, children: "Continue \u2192" }) })] }));
}
